<script setup lang="ts">
/**
 * Один созвон в панели записей серии (макет 12-23).
 *
 * Свёрнут по умолчанию: серия живёт месяцами, и поднимать расшифровку каждого
 * её созвона ради списка дат значит ждать десяток запросов там, где нужен
 * один. Раскрытие догружает ленту этой встречи — и только её.
 */
import { useToast } from '@vinprop/design-system'
import { CalendarPlus, ChevronDown, ChevronRight, Undo2, Users } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { errorMessage } from '@/api/errors'

import { listNotes, type Note } from '../notes'
import { useMeetingStore } from '../store'

import MeetingActions from './MeetingActions.vue'
import MeetingTranscript from './MeetingTranscript.vue'

import type { MeetingSession } from '../api'

const props = defineProps<{
  meeting: MeetingSession
  /** Жива ли серия этого созвона — панель считает по всем созвонам корня */
  seriesLive: boolean
}>()

/** Прошедший отменённый созвон планируют снова — форму открывает панель */
const emit = defineEmits<{ replan: [meeting: MeetingSession] }>()

const { t, locale } = useI18n()
const toast = useToast()
const store = useMeetingStore()

const isOpen = ref(false)
const notes = ref<Note[]>([])
const isLoading = ref(false)
const failed = ref(false)

/** Дата созвона — заголовок строки: по ней ищут нужный разговор */
const when = computed(() => {
  const start = props.meeting.scheduledStart || props.meeting.createdAt

  return new Date(start).toLocaleString(locale.value, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
})

/**
 * Что было на созвоне: статус и чем он кончился.
 *
 * У отменённого и «без записи» раскрывать нечего — там нет ни конспекта, ни
 * расшифровки, и вместо пустого раскрытия строка объясняет причину.
 */
const isEmpty = computed(
  () =>
    props.meeting.meetingStatus === 'CANCELLED' || props.meeting.meetingStatus === 'NO_RECORDING',
)

/**
 * Созвон ещё впереди — раскрывать в нём нечего.
 *
 * Его строка ведёт в саму встречу: туда пишут повестку и оттуда подключаются
 * к Meet. Прошедшие, наоборот, раскрываются на месте — за их конспектом и
 * пришли в историю серии.
 */
const isUpcoming = computed(
  () =>
    !props.meeting.startedAt &&
    props.meeting.meetingStatus !== 'CANCELLED' &&
    !!props.meeting.scheduledEnd &&
    new Date(props.meeting.scheduledEnd) >= new Date(),
)

const statusLabel = computed(() => {
  switch (props.meeting.meetingStatus) {
    case 'READY':
      return t('meetings.status.ready')
    case 'NO_RECORDING':
      return t('meetings.status.noRecording')
    case 'CANCELLED':
      return t('meetings.status.cancelled')
    case 'FAILED':
      return t('meetings.status.failed')
    case 'PROCESSING':
    case 'ENDED':
      return t('meetings.status.processing')
    default:
      return t('meetings.series.upcoming')
  }
})

/** Кто был на созвоне: присутствие, а не членство (DEV-2132) */
const attended = computed(() =>
  props.meeting.participants
    .filter((participant) => participant.joinedCall)
    .map((participant) => participant.name)
    .filter(Boolean),
)

/**
 * Отменённый созвон, который ещё впереди, автор возвращает прямо отсюда
 * (DEV-2193): историю серии и читают для того, чтобы увидеть выпавшую неделю.
 *
 * Живость — по серии самого созвона, а не по корню (seriesActive): правка
 * «эта и следующие» закрывает серию и заводит новую с тем же корнем, так что
 * корень жив, а вхождения этого созвона в правиле повтора уже нет — вернуть
 * его нельзя (ADR-0057, DEV-2220). Серию, отменённую «этой и следующими»,
 * возобновляют из шапки панели (DEV-2200).
 */
const canRestore = computed(
  () =>
    props.meeting.iAmAuthor &&
    props.meeting.meetingStatus === 'CANCELLED' &&
    props.seriesLive &&
    !!props.meeting.scheduledEnd &&
    new Date(props.meeting.scheduledEnd) > new Date(),
)

/**
 * Прошедший отменённый созвон вернуть уже нечем — его планируют снова
 * (DEV-2200): новая встреча встаёт в историю той же серии. Пока у серии есть
 * созвон впереди, «снова» ей не нужно — она и так живая.
 */
const canReplan = computed(
  () =>
    props.meeting.iAmAuthor &&
    props.meeting.meetingStatus === 'CANCELLED' &&
    !props.meeting.seriesActive &&
    !!props.meeting.scheduledEnd &&
    new Date(props.meeting.scheduledEnd) <= new Date(),
)

const isRestoring = ref(false)

const restore = async () => {
  isRestoring.value = true

  try {
    await store.restore(props.meeting.id)
    toast.success(t('meetings.actions.restored'))
  } catch (failure) {
    // Причин отказа несколько — серия закрыта, события больше нет, созвон
    // прошёл, — и человеку нужна та, что про его случай.
    toast.error(errorMessage(failure, t('meetings.actions.restoreFailed')))
  } finally {
    isRestoring.value = false
  }
}

const summary = computed(() => notes.value.find((note) => note.kind === 'ai_summary') ?? null)
const actions = computed(() => notes.value.filter((note) => note.kind === 'ai_action'))

const toggle = async () => {
  if (isEmpty.value) return

  isOpen.value = !isOpen.value

  if (!isOpen.value || notes.value.length || isLoading.value) return

  isLoading.value = true
  failed.value = false

  try {
    const page = await listNotes(props.meeting.id)

    notes.value = page.items
  } catch {
    // Отказ показываем строкой, а не тостом: раскрытых созвонов может быть
    // несколько, и тост не скажет, какой именно не открылся.
    failed.value = true
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <article class="sinst" :class="{ 'sinst--empty': isEmpty }">
    <RouterLink
      v-if="isUpcoming"
      class="sinst__head sinst__head--link"
      :to="`/meetings/${meeting.id}`"
    >
      <span class="sinst__when">{{ when }}</span>
      <span class="sinst__status">{{ statusLabel }}</span>

      <span v-if="meeting.noteCount" class="sinst__notes">
        {{ t('meetings.series.noteCount', meeting.noteCount) }}
      </span>

      <span class="sinst__open">{{ t('meetings.series.openCall') }}</span>
    </RouterLink>

    <button v-else type="button" class="sinst__head" :disabled="isEmpty" @click="toggle()">
      <ChevronDown v-if="isOpen" :size="14" aria-hidden="true" />
      <ChevronRight v-else-if="!isEmpty" :size="14" aria-hidden="true" />

      <span class="sinst__when">{{ when }}</span>
      <span class="sinst__status">{{ statusLabel }}</span>

      <span v-if="attended.length" class="sinst__people">
        <Users :size="12" aria-hidden="true" />
        {{ attended.join(', ') }}
      </span>

      <span v-if="meeting.noteCount" class="sinst__notes">
        {{ t('meetings.series.noteCount', meeting.noteCount) }}
      </span>

      <!-- Ключи задач видно не раскрывая: за ними и приходят в историю серии.
           Открываются соседней вкладкой — панель серии листают дальше, и
           терять её место ради одной задачи незачем. -->
      <span v-if="meeting.issueKeys.length" class="sinst__issues">
        <RouterLink
          v-for="key in meeting.issueKeys"
          :key="key"
          class="sinst__issue"
          :to="`/issue/${key}`"
          target="_blank"
          rel="noopener"
          @click.stop
        >
          {{ key }}
        </RouterLink>
      </span>
    </button>

    <p v-if="isEmpty && meeting.failureReason" class="sinst__reason">
      {{ meeting.failureReason }}
    </p>

    <button
      v-if="canRestore"
      type="button"
      class="sinst__restore"
      :disabled="isRestoring"
      @click.stop="restore()"
    >
      <Undo2 :size="12" aria-hidden="true" />
      {{ t('meetings.actions.restore') }}
    </button>

    <button
      v-if="canReplan"
      type="button"
      class="sinst__replan"
      @click.stop="emit('replan', meeting)"
    >
      <CalendarPlus :size="12" aria-hidden="true" />
      {{ t('meetings.actions.replan') }}
    </button>

    <div v-if="isOpen" class="sinst__body">
      <p v-if="isLoading" class="sinst__hint">{{ t('meetings.loading') }}</p>
      <p v-else-if="failed" class="sinst__hint">{{ t('meetings.series.loadFailed') }}</p>

      <template v-else>
        <p v-if="summary" class="sinst__summary">{{ summary.body }}</p>

        <MeetingActions :actions="actions" :project-id="meeting.projectId" />

        <MeetingTranscript
          v-if="meeting.meetingStatus === 'READY'"
          :session-id="meeting.id"
          :speakers-inferred="summary?.ai?.speakersInferred ?? false"
        />

        <p v-if="!summary && !actions.length" class="sinst__hint">
          {{ t('meetings.series.noDigest') }}
        </p>
      </template>
    </div>
  </article>
</template>

<style scoped>
.sinst {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 0;
  border-bottom: 1px solid var(--color-border);
}

.sinst__head {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-primary);
  font-family: inherit;
  font-size: var(--font-size-body-sm);
  text-align: left;
  cursor: pointer;
}

.sinst__head:disabled {
  cursor: default;
}

.sinst__head--link {
  text-decoration: none;
}

/* «Открыть» прижата вправо: строка читается как дата и статус, а переход —
   это действие, и оно стоит там, где его ищут. */
.sinst__open {
  margin-left: auto;
  color: var(--color-primary);
  font-size: var(--font-size-caption);
}

.sinst__when {
  font-weight: var(--font-weight-medium);
}

.sinst__status,
.sinst__people,
.sinst__notes {
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}

.sinst__people {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* Ключи задач прижаты к правому краю: их читают столбиком, переводя взгляд
   по датам, а не выискивая в середине строки. */
.sinst__issues {
  display: inline-flex;
  gap: 6px;
  margin-left: auto;
}

.sinst__issue {
  color: var(--color-primary);
  font-family: var(--font-family-mono, monospace);
  font-size: var(--font-size-overline);
  text-decoration: none;
}

.sinst__issue:hover {
  text-decoration: underline;
}

.sinst__restore,
.sinst__replan {
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-primary);
  font-family: inherit;
  font-size: var(--font-size-overline);
  line-height: 18px;
  cursor: pointer;
}

.sinst__restore:disabled {
  opacity: 0.6;
  cursor: default;
}

.sinst__reason,
.sinst__hint {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}

.sinst__summary {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  line-height: 20px;
  white-space: pre-line;
}

.sinst__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-left: 22px;
}
</style>
